import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";

type Bp = {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string | null;
  sector: string | null;
  stage: string | null;
  amount: string | null;
  pitch: string;
  detail: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  locale: string | null;
  ip: string | null;
  handled: boolean;
  handled_at: string | null;
  handled_by_email: string | null;
  created_at: string;
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 16, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
      <div className="muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

function RowPair({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>{children}</div>
  );
}

export function BpDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<Bp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    api.get<Bp>(`/api/admin/bp/${id}`).then(setData).catch((e) => setError(String(e)));
  }, [id]);

  const toggle = async () => {
    if (!data) return;
    setActing(true);
    try {
      const updated = await api.patch<Bp>(`/api/admin/bp/${data.id}`, { handled: !data.handled });
      setData(updated);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e));
    } finally {
      setActing(false);
    }
  };

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="muted">加载中…</div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">BP 详情 #{data.id}</div>
        <div className="page-actions">
          <button className="btn" onClick={() => nav("/bp")}>← 返回列表</button>
          <button className={`btn ${data.handled ? "" : "btn-primary"}`} onClick={toggle} disabled={acting}>
            {acting ? "处理中…" : (data.handled ? "撤销已处理" : "标记已处理")}
          </button>
        </div>
      </div>

      <div className="form">
        <RowPair>
          <Row label="创始人">{data.name}</Row>
          <Row label="公司">{data.company}</Row>
        </RowPair>
        <RowPair>
          <Row label="邮箱"><a href={`mailto:${data.email}`}>{data.email}</a></Row>
          <Row label="电话 / 微信">{data.phone || <span className="muted">—</span>}</Row>
        </RowPair>
        <RowPair>
          <Row label="赛道">{data.sector || <span className="muted">—</span>}</Row>
          <Row label="提交时间">{new Date(data.created_at).toLocaleString()}</Row>
        </RowPair>
        <RowPair>
          <Row label="融资阶段">{data.stage || <span className="muted">—</span>}</Row>
          <Row label="金额 · 估值">{data.amount || <span className="muted">—</span>}</Row>
        </RowPair>
        <RowPair>
          <Row label="来源 IP">{data.ip || <span className="muted">—</span>}</Row>
          <Row label="处理状态">
            {data.handled ? (
              <span>
                <span className="muted">已处理</span>
                {data.handled_at && <span className="muted" style={{ marginLeft: 8 }}>· {new Date(data.handled_at).toLocaleString()}</span>}
                {data.handled_by_email && <span className="muted" style={{ marginLeft: 8 }}>· {data.handled_by_email}</span>}
              </span>
            ) : (
              <span className="success">● 未处理</span>
            )}
          </Row>
        </RowPair>
        <Row label="一句话事业">
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{data.pitch}</div>
        </Row>
        <Row label="BP 附件">
          {data.attachment_url
            ? <a href={data.attachment_url} target="_blank" rel="noopener noreferrer" className="slug">
                {data.attachment_name || data.attachment_url} ↗
              </a>
            : <span className="muted">未上传</span>}
        </Row>
        <Row label="详细说明">
          {data.detail
            ? <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{data.detail}</div>
            : <span className="muted">—</span>}
        </Row>
      </div>
    </div>
  );
}
